import React, { useState } from "react"
import moment from 'moment'
import { useInfiniteScroll } from '../../../hooks/useInfiniteScroll'

import AdminTable from '../../../components/AdminTable'
import InventoryModal from './InventoryModal'
import ConfirmationModal from "../../../components/ConfirmationModal"
import { addInventoryItem, updateInventoryItem, deleteInventoryItem } from "../../../lib/actions"
import { fetchInventory } from "../../../lib/queries"
import TicketCSVUploadModal from "./TicketCSVUploadModal"

const CREATE_ACTION = "create"
const UPDATE_ACTION = "update"
const DELETE_ACTION = "delete"

function TicketsTable() {
    const [searchTerm, setSearchTerm] = useState("")
    const [showModal, setShowModal] = useState(false)
    const [showCsvModal, setShowCsvModal] = useState(false)

    const {
        items: tickets,
        ref,
        isLoading,
        isError,
        error,
        isFetchingNextPage,
        refetch,
        hasNextPage
    } = useInfiniteScroll('inventory', fetchInventory)

    const toggleModal = (modalName) => {
        if (modalName === showModal) {
            setShowModal(false)
        } else {
            setShowModal(modalName)
        }
    }

    const handleSubmit = async (data, action) => {
        try {
            if (action === UPDATE_ACTION) {
                await updateInventoryItem(data.id, data)
            } else if (action === CREATE_ACTION) {
                await addInventoryItem(data)
            } else if (action === DELETE_ACTION) {
                await deleteInventoryItem(data.id)
            }
            await refetch()
            setShowModal(false)
        } catch (error) {
            console.error("Error saving inventory:", error)
        }
    }

    const isPastFlight = (departureDate) => {
        return moment(departureDate).isBefore(moment().startOf('day'))
    }

    const filteredTickets = tickets.filter((ticket) =>
        ticket.flightNo.toLowerCase().includes(searchTerm.toLowerCase())
    )

    if (isLoading) return <div>Loading...</div>
    if (isError) return <div>Error: {error.message}</div>

    return (
        <section>
            <div className="row mb-3">
                <div className="col-md-4">
                    <input
                        type="text"
                        className="form-control"
                        placeholder="Search by flight number..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                </div>
                {showModal === "createTicket" && <InventoryModal
                    isOpen={showModal === "createTicket"}
                    onClose={() => toggleModal("createTicket")}
                    mode={CREATE_ACTION}
                    onSubmit={(data) => handleSubmit(data, CREATE_ACTION)}
                />}
                <div className="col-md-4 ms-auto text-end">
                    <button
                        className="btn btn-primary"
                        onClick={() => toggleModal("createTicket")}
                    >
                        Add New Ticket
                    </button>&nbsp;&nbsp;
                    <button
                        className="btn btn-success"
                        onClick={() => setShowCsvModal(true)}
                    >
                        Upload CSV
                    </button>
                </div>
            </div>

            <AdminTable>
                <thead className="thead-light">
                    <tr>
                        <th>#</th>
                        <th>Flight No</th>
                        <th>Departure Date</th>
                        <th>Departure Time</th>
                        <th>Arrival Time</th>
                        <th>Available Tickets</th>
                        <th>Price</th>
                        <th>Actions</th>
                    </tr>
                </thead>
                <tbody>
                    {filteredTickets.map((ticket, index) => (
                        <tr key={ticket.id} className={isPastFlight(ticket.departureDate) ? 'table-secondary' : ''}>
                            <td>{index + 1}</td>
                            <td>{ticket.flightNo}</td>
                            <td>{moment(ticket.departureDate).format('DD/MM/YYYY')}</td>
                            <td>{ticket.departureTime}</td>
                            <td>{ticket.arrivalTime}</td>
                            <td>{ticket.availableTickets}</td>
                            <td>${ticket.price}</td>
                            <td>
                                {showModal === "editTicket" + index && <InventoryModal
                                    isOpen={showModal === "editTicket" + index}
                                    onClose={() => toggleModal("editTicket" + index)}
                                    mode={UPDATE_ACTION}
                                    inventoryData={ticket}
                                    onSubmit={(data) => handleSubmit(data, UPDATE_ACTION)}
                                />}
                                {showModal === "deleteTicket" + index && <ConfirmationModal
                                    isOpen={showModal === "deleteTicket" + index}
                                    onClose={() => toggleModal("deleteTicket" + index)}
                                    actionBtnText="Delete"
                                    onAction={() => handleSubmit(ticket, DELETE_ACTION)}
                                    title="Delete Ticket"
                                    body="Are you sure you want to delete this ticket?"
                                />}
                                <button
                                    className="btn btn-sm btn-outline-primary me-2"
                                    onClick={() => toggleModal("editTicket" + index)}
                                    disabled={isPastFlight(ticket.departureDate)}
                                >
                                    Edit
                                </button>
                                <button
                                    className="btn btn-sm btn-outline-danger"
                                    onClick={() => toggleModal("deleteTicket" + index)}
                                    disabled={isPastFlight(ticket.departureDate)}
                                >
                                    Delete
                                </button>
                            </td>
                        </tr>
                    ))}
                    <tr ref={ref}>
                        <td colSpan="8">
                            {isFetchingNextPage ? (
                                <div className="text-center py-2">
                                    <div className="spinner-border spinner-border-sm" role="status">
                                        <span className="visually-hidden">Loading...</span>
                                    </div>
                                </div>
                            ) : hasNextPage ? (
                                <div className="text-center py-2 text-muted">
                                    Scroll for more...
                                </div>
                            ) : filteredTickets.length > 0 ? (
                                <div className="text-center py-2 text-muted">
                                    No more records to load
                                </div>
                            ) : (
                                <div className="text-center py-2 text-muted">
                                    No records found
                                </div>
                            )}
                        </td>
                    </tr>
                </tbody>
            </AdminTable>

            <TicketCSVUploadModal
                isOpen={showCsvModal}
                onClose={() => setShowCsvModal(false)}
                existingFlights={tickets}
                onComplete={() => {
                    refetch()
                    setShowCsvModal(false)
                }}
            />
        </section>
    )
}

export default TicketsTable
